import React, { useState, useEffect, useContext, } from 'react';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, Grid, IconButton, InputLabel, Menu, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DialogTransition from '../DialogTransition/DialogTansition';
import { FetchContext } from '../../features/contexts/fetch';
import { AxiosInstance } from 'axios';
import { useDispatch } from 'react-redux';
import { TNtfyNew } from '../../_types/ntfy';
import { NtfyReport } from '../../features/store/ntfy.slice';

export interface FetchContextType {
  authAxios: any;
  isAdmin: boolean;
  canAdd: boolean;
  canManageAdasSubscriptions: boolean;
  canReadAll: boolean;
  canCreateSecurityCodes: boolean;
  canManageUsers: boolean;
  canManageAdasPackages: boolean;
  canManageAdasTargets: boolean;
  canManageIsoRigs: boolean;
}

export interface IIsoRig {
  ID: number;
  ISO_Location: string;
  ISO_Model_Number: string;
  ISO_Rig_Number: number;
  LFToe: number;
  RFToe: number;
  LRToe: number;
  RRToe: number;
  LFCamber: number;
  RFCamber: number;
  LRCamber: number;
  RRCamber: number;
  ToeTolerance: number;
  CamberTolerance: number;
  TotalToeTolerance: number;
  ThrustTolerance: number;
  SteerTolerance: number;
  WifiSSID: string;
  LastUpdated: string;
  linearSensorTolerance: number | string | null;
  runoutRangeTolerance: number | string | null;
  bcs_btcs: string | null;
  golden_ccs_acs: string | null;
  golden_ccs_vcs: string | null;
  golden_floorcs_vcs: string | null;
  golden_level_acs: string | null;
  golden_rfwcent_vcs: string | null;
  frameTolerances: string | null;
  rctpCal: string | null;
  Gccs_G12x12tcsCal: string | null;
  ISO_Label: string | null;
}

export const InitialRigState: Partial<IIsoRig> = {
  ISO_Location: '', ISO_Model_Number: '', ISO_Rig_Number: 1,
  LFToe: 0, RFToe: 0, LRToe: 0, RRToe: 0,
  LFCamber: 0, RFCamber: 0, LRCamber: 0, RRCamber: 0,
  linearSensorTolerance: 0.5, runoutRangeTolerance: 0.5,
  WifiSSID: '', ISO_Label: '',
};

export const DefaultRig: Partial<IIsoRig> = {
  ISO_Location: '', ISO_Model_Number: '', ISO_Rig_Number: 0,
  LFToe: 0, RFToe: 0, LRToe: 0, RRToe: 0,
  LFCamber: 0, RFCamber: 0, LRCamber: 0, RRCamber: 0,
  ToeTolerance: 0, CamberTolerance: 0, TotalToeTolerance: 0, ThrustTolerance: 0, SteerTolerance: 0,
  WifiSSID: '', LastUpdated: new Date().toISOString(),
  linearSensorTolerance: '0',
  runoutRangeTolerance: '0',
  bcs_btcs: '0',
  golden_ccs_acs: '0',
  golden_ccs_vcs: '0',
  golden_floorcs_vcs: '0',
  golden_level_acs: '0',
  golden_rfwcent_vcs: '0',
  frameTolerances: '0',
  rctpCal: '0',
  Gccs_G12x12tcsCal: '0',
  ISO_Label: '0',
};


// Golden Values Dialog
const GoldenValuesDialog = ({ open, rig, onClose, onEdit,
}: {
  open: boolean;
  rig: IIsoRig | null;
  onClose: () => void;
  onEdit: () => void;
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!rig) return null;

  const handleCopy = (value: string | number | null, label: string) => {
    navigator.clipboard.writeText(String(value ?? ''));
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const goldenStringFields = [
    { label: 'BCS / BTCS', value: rig.bcs_btcs },
    { label: 'Golden CCS ACS', value: rig.golden_ccs_acs },
    { label: 'Golden CCS VCS', value: rig.golden_ccs_vcs },
    { label: 'Golden Floor CS VCS', value: rig.golden_floorcs_vcs },
    { label: 'Golden Level ACS', value: rig.golden_level_acs },
    { label: 'Golden RFW Cent VCS', value: rig.golden_rfwcent_vcs },
    { label: 'Frame Tolerances', value: rig.frameTolerances },
    { label: 'RCTP Cal', value: rig.rctpCal },
    { label: 'Gccs G12x12 TCS Cal', value: rig.Gccs_G12x12tcsCal },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Golden Values — Rig {rig.ID}
          </Typography>
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            Last Updated: {rig.LastUpdated}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Calibration Tolerances
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Linear Sensor Tolerance
              </Typography>
              <Typography variant="h6">
                {rig.linearSensorTolerance ?? 'N/A'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Runout Range Tolerance
              </Typography>
              <Typography variant="h6">
                {rig.runoutRangeTolerance ?? 'N/A'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }}>Calibration Arrays</Divider>

        <Grid container spacing={2}>
          {goldenStringFields.map((field) => (
            <Grid item xs={12} key={field.label}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
                    {field.label}
                  </Typography>

                  <IconButton size="small" onClick={() => handleCopy(field.value, field.label)}>
                    {copiedField === field.label ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Stack>

                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, maxHeight: 80, overflowY: 'auto', }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem',
                    wordBreak: 'break-all', }}> {field.value || 'N/A'} </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>

        <Button variant="outlined" onClick={onEdit}>Edit Golden Values</Button>

        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Add ISO Rig Dialog
const AddRigDialog = ({ open, onClose, onSave, locations,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (newRig: Partial<IIsoRig>) => Promise<void>;
  locations: string[];
}) => {
  const [form, setForm] = useState<Partial<IIsoRig>>(InitialRigState);
  const [saving, setSaving] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm(InitialRigState);
    }
  }, [open]);

  const handleChange = (field: keyof IIsoRig, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.ISO_Location || !form.ISO_Model_Number) return;

    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New ISO Rig</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Identity Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Identity
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select label="Location" value={form.ISO_Location ?? ''}
                onChange={(e) => handleChange('ISO_Location', e.target.value)}>
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField label="Model (e.g. V3300)" size="small" fullWidth value={form.ISO_Model_Number ?? ''}
              onChange={(e) => handleChange('ISO_Model_Number', e.target.value) } />
          </Grid>

          <Grid item xs={4}>
            <TextField label="Rig #" size="small" type="number" fullWidth value={form.ISO_Rig_Number ?? 1}
              onChange={(e) => handleChange('ISO_Rig_Number', Number(e.target.value)) } />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Initial Toe Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Initial Toe
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {['LFToe', 'RFToe', 'LRToe', 'RRToe'].map((field) => (
            <Grid item xs={3} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig] ?? 0}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Initial Camber Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Initial Camber
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {['LFCamber', 'RFCamber', 'LRCamber', 'RRCamber'].map((field) => (
            <Grid item xs={3} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig] ?? 0}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Alignment Tolerances Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Alignment Tolerances
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            'ToeTolerance',
            'CamberTolerance',
            'TotalToeTolerance',
            'ThrustTolerance',
            'SteerTolerance',
          ].map((field) => (
            <Grid item xs={6} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig] ?? 0}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Metadata Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Metadata
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Wifi SSID" size="small" fullWidth value={form.WifiSSID ?? ''}
              onChange={(e) => handleChange('WifiSSID', e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="ISO Label" size="small" fullWidth value={form.ISO_Label ?? ''}
              onChange={(e) => handleChange('ISO_Label', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={ saving || !form.ISO_Location || !form.ISO_Model_Number }
          onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Create Rig'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit ISO Rig Dialog
const EditRigDialog = ({ open, rig, onClose, onSave,
}: {
  open: boolean;
  rig: IIsoRig | null;
  onClose: () => void;
  onSave: (updated: IIsoRig) => Promise<void>;
}) => {
  const [form, setForm] = useState<IIsoRig | null>(null);
  const [saving, setSaving] = useState(false);

  // Load rig into form when opened
  useEffect(() => {
    if (rig && open) {
      setForm(rig);
    }
  }, [rig, open]);

  if (!form) return null;

  const handleChange = (field: keyof IIsoRig, value: any) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Rig Specifications</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Identity Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Identity
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <TextField label="Location" size="small" fullWidth value={form.ISO_Location}
              onChange={(e) => handleChange('ISO_Location', e.target.value)} />
          </Grid>

          <Grid item xs={4}>
            <TextField label="Model" size="small" fullWidth value={form.ISO_Model_Number}
              onChange={(e) => handleChange('ISO_Model_Number', e.target.value) } />
          </Grid>

          <Grid item xs={4}>
            <TextField label="Rig #" size="small" type="number" fullWidth value={form.ISO_Rig_Number}
              onChange={(e) => handleChange('ISO_Rig_Number', Number(e.target.value)) } />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Toe Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Toe Settings
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {['LFToe', 'RFToe', 'LRToe', 'RRToe'].map((field) => (
            <Grid item xs={3} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig]}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Camber Section */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Camber Settings
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {['LFCamber', 'RFCamber', 'LRCamber', 'RRCamber'].map((field) => (
            <Grid item xs={3} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig]}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Alignment Tolerances */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Alignment Tolerances
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            'ToeTolerance',
            'CamberTolerance',
            'TotalToeTolerance',
            'ThrustTolerance',
            'SteerTolerance',
          ].map((field) => (
            <Grid item xs={6} key={field}>
              <TextField label={field} size="small" type="number" fullWidth value={form[field as keyof IIsoRig]}
                onChange={(e) => handleChange(field as keyof IIsoRig, Number(e.target.value)) } />
            </Grid>
          ))}
        </Grid>

        {/* Metadata */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Metadata
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Wifi SSID" size="small" fullWidth value={form.WifiSSID}
              onChange={(e) => handleChange('WifiSSID', e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="ISO Label" size="small" fullWidth value={form.ISO_Label ?? ''}
              onChange={(e) => handleChange('ISO_Label', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={saving} onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Golden Values Dialog
const EditGoldenValuesDialog = ({ open, rig, onClose, onSave,
}: {
  open: boolean;
  rig: IIsoRig | null;
  onClose: () => void;
  onSave: (updated: IIsoRig) => Promise<void>;
}) => {
  const [form, setForm] = useState<IIsoRig | null>(null);
  const [saving, setSaving] = useState(false);

  // Load rig into form when dialog opens
  useEffect(() => {
    if (rig && open) {
      setForm(rig);
    }
  }, [rig, open]);

  if (!form) return null;

  const handleChange = (field: keyof IIsoRig, value: any) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Golden Values</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Calibration Tolerances */}
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Calibration Tolerances
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField label="Linear Sensor Tolerance" size="small"
              type="number" fullWidth value={form.linearSensorTolerance ?? ''}
              onChange={(e) => handleChange('linearSensorTolerance', Number(e.target.value)) } />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Runout Range Tolerance" size="small"
              type="number" fullWidth value={form.runoutRangeTolerance ?? ''}
              onChange={(e) => handleChange('runoutRangeTolerance', Number(e.target.value)) } />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }}>Golden Value Strings</Divider>

        {/* Golden Value Strings */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            'bcs_btcs',
            'golden_ccs_acs',
            'golden_ccs_vcs',
            'golden_floorcs_vcs',
            'golden_level_acs',
            'golden_rfwcent_vcs',
            'frameTolerances',
            'rctpCal',
            'Gccs_G12x12tcsCal',
          ].map((field) => (
            <Grid item xs={12} key={field}>
              <TextField label={field} size="small" fullWidth multiline minRows={2}
                value={form[field as keyof IIsoRig] ?? ''}
                onChange={(e) => handleChange(field as keyof IIsoRig, e.target.value) } />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={saving} onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteRigDialog = ({ open, rig, onClose, onDelete,
}: {
  open: boolean;
  rig: IIsoRig | null;
  onClose: () => void;
  onDelete: () => void;
}) => {
  const [countdown, setCountdown] = useState(5);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;

    setCountdown(5);
    setChecked(false);

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete ISO Rig</DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to permanently delete this ISO Rig:
        </Typography>

        <Typography variant="subtitle1" fontWeight="bold">
          {rig?.ISO_Location} / {rig?.ISO_Model_Number} / #{rig?.ISO_Rig_Number}
        </Typography>

        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          This action is immediate and cannot be undone.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="I understand that this is immediate and permanent" sx={{ mt: 2 }} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" color="error" disabled={countdown > 0 || !checked} onClick={onDelete}>
          {countdown > 0 ? `Delete (${countdown})` : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// Main Component
const ManageIsoRigsDialog = ({ open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<any>();
  const fetchContext = useContext(FetchContext) as {
    authAxios?: AxiosInstance | null;
    isAdmin: boolean;
    canAdd: boolean;
    canCreateSecurityCodes: boolean;
    canManageUsers: boolean;
    canManageIsoRigs: boolean;
  };

  const [rows, setRows] = useState<IIsoRig[]>([]);
  const [loading, setLoading] = useState(false);

  const [locations, setLocations] = useState<string[]>([]);
  const [availableRigData, setAvailableRigData] = useState<any[]>([]);

  const [selection, setSelection] = useState({ location: '', model: '', rigNum: '', });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [selectedRig, setSelectedRig] = useState<IIsoRig | null>(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openGolden, setOpenGolden] = useState(false);
  const [openEditGolden, setOpenEditGolden] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const models = Array.from(
    new Set(availableRigData.map((i: any) => i.ISO_Model_Number))
  );

  const exportPrefix = `${selection.location || 'ALL'}_${selection.model || 'ALL'}_${selection.rigNum || 'ALL'}`;


  const rigNums = availableRigData
    .filter((i: any) => i.ISO_Model_Number === selection.model)
    .map((i: any) => i.ISO_Rig_Number);

  useEffect(() => {
    if (!open || !fetchContext.authAxios) return;

    setSelection({ location: '', model: '', rigNum: '' });
    setRows([]);
    setSelectedRig(null);

    fetchContext.authAxios
      .post('/Modules/AlignerUtilities.aspx/GetDistinctListIsoLocation', {})
      .then((res) => {
        try {
          const parsed = JSON.parse(res.data.d);
          setLocations(parsed.map((i: any) => i.ISO_Location));
        } catch {
          dispatch(
            NtfyReport(
              TNtfyNew('Failed to parse locations response', 'error', 'short')
            )
          );
        }
      })
      .catch(() => {
        dispatch(
          NtfyReport(TNtfyNew('Failed to load locations', 'error', 'short'))
        );
      });
  }, [open, fetchContext.authAxios, dispatch]);

  const handleLocationChange = async (loc: string) => {
    if (!fetchContext.authAxios) return;

    setSelection({ location: loc, model: '', rigNum: '' });

    try {
      const res = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/GetIsoModelIsoRigNumByLocation',
        { sLocation: loc }
      );
      setAvailableRigData(JSON.parse(res.data.d));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = async () => {
    if (!fetchContext.authAxios) return;

    setLoading(true);

    try {
      const res = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/GetISORigDataForUI',
        {
          sRigLocation: selection.location,
          sRigNumber: Number(selection.rigNum),
          sSystemModel: selection.model,
        }
      );

      setRows(JSON.parse(res.data.d));
      setPage(0);
    } catch {
      dispatch(NtfyReport(TNtfyNew('Search failed', 'error', 'short')));
    } finally {
      setLoading(false);
    }
  };

  const sanitizeRig = (rig: Partial<IIsoRig>): IIsoRig => {
    const full: any = { ...rig };

    const zeroIfNullFields: (keyof IIsoRig)[] = [
      'linearSensorTolerance',
      'runoutRangeTolerance',
      'bcs_btcs',
      'golden_ccs_acs',
      'golden_ccs_vcs',
      'golden_floorcs_vcs',
      'golden_level_acs',
      'golden_rfwcent_vcs',
      'frameTolerances',
      'rctpCal',
      'Gccs_G12x12tcsCal',
      'ISO_Label',
    ];

    zeroIfNullFields.forEach((key) => {
      const val = full[key];
      if (val === null || val === undefined || val === '' || Number.isNaN(val)) {
        full[key] = '0';
      }
    });

    Object.keys(full).forEach((key) => {
      const k = key as keyof IIsoRig;
      if (!zeroIfNullFields.includes(k) && (full[k] === null || full[k] === undefined)) {
        full[k] = '';
      }
    });

    return full as IIsoRig;
  };

  const handleSaveEdit = async (updated: IIsoRig) => {
    if (!fetchContext.authAxios) return;

    const cleanUpdated = sanitizeRig(updated);

    try {
      const res = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/UpdateIsoRig',
        {
          IsoRigPayload: cleanUpdated,
          sISORig_ID: cleanUpdated.ID,
          ISO_Location: cleanUpdated.ISO_Location,
          ISO_Model_Number: cleanUpdated.ISO_Model_Number,
          ISO_Rig_Number: cleanUpdated.ISO_Rig_Number,
        }
      );

      if (res.data.d.includes('<PASS>')) {
        setOpenEdit(false);
        setOpenEditGolden(false);

        handleSearch();

        dispatch(
          NtfyReport(TNtfyNew('Rig specifications updated', 'success', 'short'))
        );
      } else {
        dispatch(NtfyReport(TNtfyNew(res.data.d, 'error', 'long')));
      }
    } catch {
      dispatch(NtfyReport(TNtfyNew('Update failed', 'error', 'short')));
    }
  };

  const handleAddRig = async (newRig: Partial<IIsoRig>) => {
    if (!fetchContext.authAxios) return;

    const merged = {
      ...DefaultRig,
      ...newRig,
      LastUpdated: new Date().toISOString(),
    };

    const cleanNew = sanitizeRig(merged);

    try {
      const res = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/AddIsoRig',
        {
          IsoRigPayload: cleanNew,
          ISO_Location: cleanNew.ISO_Location,
          ISO_Model_Number: cleanNew.ISO_Model_Number,
          ISO_Rig_Number: cleanNew.ISO_Rig_Number,
        }
      );

      if (res.data.d.includes('<PASS>')) {
        dispatch(NtfyReport(TNtfyNew('New Rig added successfully', 'success', 'short')));
        setOpenAdd(false);
        handleSearch();
      } else {
        dispatch(NtfyReport(TNtfyNew(res.data.d, 'error', 'long')));
      }
    } catch {
      dispatch(NtfyReport(TNtfyNew('Failed to add rig', 'error', 'short')));
    }
  };

  const handleDeleteRig = async () => {
    if (!fetchContext.authAxios || !selectedRig) return;

    try {
      const res = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/DeleteISORigRecordByID',
        {
          sISORig_ID: selectedRig.ID
        }
      );

      if (res.data.d.includes('<PASS>')) {
        dispatch(
          NtfyReport(TNtfyNew('Rig deleted successfully', 'success', 'short'))
        );
        setOpenDelete(false);
        handleSearch(); // refresh table
      } else {
        dispatch(NtfyReport(TNtfyNew(res.data.d, 'error', 'long')));
      }
    } catch {
      dispatch(NtfyReport(TNtfyNew('Failed to delete rig', 'error', 'short')));
    }
  };

  const renderSearchFilters = () => (
    <Box sx={{ mb: 3, mt: 1 }}>
      <Grid container spacing={2} alignItems="center">

        {/* Location */}
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="location-label">Location</InputLabel>
            <Select labelId="location-label" id="location-select" value={selection.location} label="Location"
              onChange={(e) => handleLocationChange(e.target.value as string)}>
              {locations.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Model */}
        <Grid item xs={3}>
          <FormControl fullWidth size="small" disabled={!selection.location}>
            <InputLabel id="model-label">Model</InputLabel>
            <Select labelId="model-label" id="model-select" value={selection.model} label="Model"
              onChange={(e) => setSelection({ ...selection, model: e.target.value as string, rigNum: '', })}>
              {models.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Rig Number */}
        <Grid item xs={3}>
          <FormControl fullWidth size="small" disabled={!selection.model}>
            <InputLabel id="rig-label">Rig #</InputLabel>
            <Select labelId="rig-label" id="rig-select" value={selection.rigNum} label="Rig #"
              onChange={(e) => setSelection({ ...selection, rigNum: e.target.value as string })}>
              {rigNums.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Search Button */}
        <Grid item xs={3}>
          <Button variant="contained" fullWidth disabled={!selection.rigNum || loading} onClick={handleSearch}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Search'
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderTableSection = () => (
    <Box>
      {/* Table */}
      <TableContainer sx={{ maxHeight: '50vh' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label / Rig</TableCell>
              <TableCell>Toe (LF/RF/LR/RR)</TableCell>
              <TableCell>Camber (LF/RF/LR/RR)</TableCell>
              <TableCell>Wifi SSID</TableCell>
              {
                fetchContext.canManageIsoRigs ? (
                  <>
                    <TableCell align="right">Actions</TableCell>
                  </>
                ) : null
              }
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress sx={{ m: 2 }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No rigs found. Adjust your search filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow key={row.ID} hover>

                    {/* ISO_Label / Rig */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {row.ISO_Label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.ISO_Model_Number} #{row.ISO_Rig_Number}
                      </Typography>
                    </TableCell>

                    {/* Toe */}
                    <TableCell>
                      {`${row.LFToe}/${row.RFToe}/${row.LRToe}/${row.RRToe}`}
                    </TableCell>

                    {/* Camber */}
                    <TableCell>
                      {`${row.LFCamber}/${row.RFCamber}/${row.LRCamber}/${row.RRCamber}`}
                    </TableCell>

                    {/* Wifi */}
                    <TableCell>{row.WifiSSID}</TableCell>

                    {/* Actions */}
                    {
                      fetchContext.canManageIsoRigs ? (
                        <>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Edit Specs">
                                <IconButton size="small" color="primary"
                                  onClick={() => { setSelectedRig(row); setOpenEdit(true); }}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Golden Values">
                                <IconButton size="small" color="secondary"
                                  onClick={() => { setSelectedRig(row); setOpenGolden(true); }}>
                                  <SettingsSuggestIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Rig">
                                <IconButton size="small" color="error"
                                  onClick={() => { setSelectedRig(row); setOpenDelete(true); }}>
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </>
                      ) : null
                    }
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {
            fetchContext.canManageIsoRigs ? (
              <>
                <Button variant="outlined" startIcon={<AddIcon />} sx={{ mr: 1}} onClick={() => setOpenAdd(true)}>
                  Add New Rig
                </Button>
              </>
            ) : null
          }
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={(e) => setExportAnchor(e.currentTarget)}>
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => { setExportAnchor(null);
                const keys = ['ID', 'ISO_Location', 'ISO_Model_Number', 'ISO_Rig_Number', 'LFToe', 'RFToe', 'LRToe',
                  'RRToe', 'LFCamber', 'RFCamber', 'LRCamber', 'RRCamber', 'ToeTolerance', 'CamberTolerance',
                  'TotalToeTolerance', 'ThrustTolerance', 'SteerTolerance', 'WifiSSID', 'LastUpdated',
                  'linearSensorTolerance', 'runoutRangeTolerance', 'bcs_btcs', 'golden_ccs_acs', 'golden_ccs_vcs',
                  'golden_floorcs_vcs', 'golden_level_acs', 'golden_rfwcent_vcs', 'frameTolerances', 'rctpCal',
                  'Gccs_G12x12tcsCal'];
                const header = keys.join(',');
                const csvRows = rows.map(
                  row => keys.map(key => `"${String(row[key as keyof IIsoRig]).replace(/"/g, '""')}"`).join(','));
                const csv = [header, ...csvRows].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${exportPrefix}_ISO_Rigs_Export.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}>Export as CSV</MenuItem>
            <MenuItem onClick={() => { setExportAnchor(null);
                const json = JSON.stringify(rows, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${exportPrefix}_ISO_Rigs_Export.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}>Export as JSON</MenuItem>
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center',}}>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }} />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog TransitionComponent={DialogTransition} open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>ISO Rig Management</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Search Filters */}
          {renderSearchFilters()}
          {/* Table + Export + Pagination */}
          {renderTableSection()}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
        {/* Add Rig */}
        <AddRigDialog open={openAdd} onClose={() => setOpenAdd(false)} onSave={handleAddRig} locations={locations} />

        {/* Edit Rig */}
        <EditRigDialog open={openEdit} rig={selectedRig} onClose={() => setOpenEdit(false)} onSave={handleSaveEdit} />

        {/* Golden Values (View Only) */}
        <GoldenValuesDialog open={openGolden} rig={selectedRig} onClose={() => setOpenGolden(false)}
          onEdit={() => { setOpenGolden(false); setOpenEditGolden(true); }} />

        {/* Edit Golden Values */}
        <EditGoldenValuesDialog open={openEditGolden} rig={selectedRig} onClose={() => setOpenEditGolden(false)}
          onSave={async (updated) => { await handleSaveEdit(updated); setOpenEditGolden(false); }} />

        <DeleteRigDialog open={openDelete} rig={selectedRig} onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteRig} />

    </>
  );
};

export default ManageIsoRigsDialog;
